import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Settings as SettingsIcon } from "lucide-react";
import axiosInstance from "../../api/axiosInstance";
import { useApp } from "../../context/formflow/AppContext";
import { ACCENT } from "../../constants/formflow/fieldTypes";

const initialForm = {
  smtp_email: "",
  smtp_email_from: "",
  smtp_password: "",
  smtp_host: "",
  smtp_port: "",
  smtp_secure: false,
};

export function SettingsPage() {
  const navigate = useNavigate();
  const { showToast } = useApp();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get("/api/formflow/settings/smtp");
        if (!data.success) {
          showToast("Failed to load settings", "error");
          return;
        }

        if (data.smtpConfig) {
          setForm({
            smtp_email: data.smtpConfig.smtp_email || "",
            smtp_email_from: data.smtpConfig.smtp_email_from || "",
            smtp_password: "",
            smtp_host: data.smtpConfig.smtp_host || "",
            smtp_port: data.smtpConfig.smtp_port || "",
            smtp_secure: Number(data.smtpConfig.smtp_secure) === 1,
          });
        }
      } catch (err) {
        console.error(err);
        showToast("Error loading settings", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [showToast]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!form.smtp_email.trim() || !form.smtp_email_from.trim() || !form.smtp_password.trim() || !form.smtp_host.trim() || !String(form.smtp_port).trim()) {
      showToast("All SMTP fields are required, including password", "error");
      return;
    }

    try {
      setSaving(true);
      const { data } = await axiosInstance.post("/api/formflow/settings/smtp", {
        ...form,
        smtp_port: Number(form.smtp_port),
        smtp_secure: form.smtp_secure ? 1 : 0,
      });

      if (!data.success) {
        showToast(data.message || "Failed to save settings", "error");
        return;
      }

      setForm((prev) => ({ ...prev, smtp_password: "" }));
      showToast("SMTP settings saved", "success");
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Error saving settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 p-8 text-center">Loading settings...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-gray-200 px-10 h-16 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/formflow")} className="flex items-center gap-2 border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-lg cursor-pointer text-sm font-semibold hover:bg-gray-50">
            <ArrowLeft size={16} /> Dashboard
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: ACCENT }}>
              <SettingsIcon size={18} color="white" />
            </div>
            <div>
              <div className="text-base font-bold text-gray-900 leading-none">Settings</div>
              <div className="text-[11px] text-gray-400">SMTP Email Configuration</div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-8 px-10 max-w-3xl mx-auto">
        <form onSubmit={handleSave} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-900 m-0 mb-1">Email Sending Settings</h1>
            <p className="text-sm text-gray-500 m-0">Password is saved in DB but is never returned to the frontend. Enter it every time you save settings.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1.5">SMTP_EMAIL</label>
              <input type="email" value={form.smtp_email} onChange={(e) => updateField("smtp_email", e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none box-border text-gray-900" placeholder="smtp-user@example.com" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1.5">SMTP_EMAIL_FROM</label>
              <input type="email" value={form.smtp_email_from} onChange={(e) => updateField("smtp_email_from", e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none box-border text-gray-900" placeholder="from@example.com" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1.5">SMTP_PASSWORD</label>
              <input type="password" value={form.smtp_password} onChange={(e) => updateField("smtp_password", e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none box-border text-gray-900" placeholder="Required every save" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1.5">SMTP_HOST</label>
              <input type="text" value={form.smtp_host} onChange={(e) => updateField("smtp_host", e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none box-border text-gray-900" placeholder="smtp.example.com" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1.5">SMTP_PORT</label>
              <input type="number" value={form.smtp_port} onChange={(e) => updateField("smtp_port", e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none box-border text-gray-900" placeholder="465" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1.5">SMTP_SECURE</label>
              <select value={form.smtp_secure ? "1" : "0"} onChange={(e) => updateField("smtp_secure", e.target.value === "1")} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none box-border text-gray-900 bg-white">
                <option value="0">false</option>
                <option value="1">true</option>
              </select>
            </div>
          </div>

          <button type="submit" disabled={saving} className="mt-6 w-full py-3 text-white border-none rounded-xl cursor-pointer text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: ACCENT }}>
            <Save size={16} /> {saving ? "Saving..." : "Save Settings"}
          </button>
        </form>
      </div>
    </div>
  );
}
